<template>
  <div class="relative" :class="textAreaClasses">
    <textarea
      :id="id"
      ref="textAreaRef"
      v-model="content"
      :status="status"
      :class="computeClasses"
      :aria-invalid="hasError"
      :aria-describedby="ariaDescribedByName"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :rows="rows"
      @input="handleInput"
      @keyup.once="$emit('keyupOnce')"
      @keydown.enter.exact="handleEnterKey"
      @blur="checkValidation()"
      @click="$emit('click')"
    />
    <label :class="`${textAreaLabelClasses} touch-none pointer-events-none`" :for="id">{{ labelName }}</label>
    <HelperText v-if="helperTextMessage" :id="id" :text="helperTextMessage" :status="helperStatus" />
  </div>
</template>

<script lang="ts">
import HelperText from '../helper_text/HelperText.vue';
import helperStatus from '../../helpers/status';

export default {
  name: 'TextArea',
  components: {
    HelperText,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    modelValue: {
      type: String,
      default: '',
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    helperText: {
      default: '',
      type: String,
    },
    characterCounter: {
      type: Boolean,
    },
    minCharacters: {
      default: 0,
      type: Number,
    },
    maxCharacters: {
      default: 0,
      type: Number,
    },
    rows: {
      type: Number,
      default: undefined,
    },
    propClasses: {
      type: String,
      default: '',
    },
    presenceRequired: {
      type: Boolean,
      default: false,
    },
    preventEnterDefault: {
      type: Boolean,
      default: false,
    },
    autoExpand: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      content: this.modelValue,
      curCharCounter: this.modelValue.length,
      helperTextMessage: this.helperText,
      helperStatus: this.status,
    };
  },
  computed: {
    ariaDescribedByName(): String | null {
      return `${this.id}-helper`;
    },
    computeClasses() {
      return ['text-area-input', this.propClasses];
    },
    textAreaClasses(): String {
      if (this.hasError) {
        return 'text-area-error';
      }
      if (this.hasWarning) {
        return 'text-area-warn';
      }
      if (this.isValid) {
        return 'text-area-valid';
      }
      return 'text-area';
    },
    textAreaLabelClasses(): String {
      if (this.content) {
        return 'text-area-label-filled';
      }
      return 'text-area-label';
    },
    hasError(): Boolean {
      return this.helperStatus === helperStatus.Error;
    },
    isValid(): Boolean {
      return this.helperStatus === helperStatus.Valid;
    },
    hasWarning(): Boolean {
      return this.helperStatus === helperStatus.Warn;
    },
  },
  watch: {
    modelValue(value: string) {
      this.content = value;
      this.$nextTick(() => {
        this.adjustHeight();
      });
    },
    content(value: String) {
      this.$emit('update:modelValue', value);
    },
    helperText(newHelperTextMessage) {
      this.helperTextMessage = newHelperTextMessage;
    },
    status(newStatus) {
      this.helperStatus = newStatus;
    },
    helperStatus(textAreaStatus) {
      this.$emit('textAreaStatus', textAreaStatus);
    },
    minCharacters() {
      this.updateCharCountMessage();
    },
    maxCharacters() {
      this.updateCharCountMessage();
    },
  },
  mounted() {
    // on component mount, create the char count message
    this.updateCharCountMessage();
  },
  methods: {
    adjustHeight() {
      if (!this.autoExpand) return;
      const textarea = this.$refs.textAreaRef;
      // Reset height to 'auto' to allow shrinking when text is deleted
      textarea.style.height = 'auto';
      // Set height to match scrollHeight
      textarea.style.height = `${textarea.scrollHeight}px`;
    },
    handleInput({ target }) {
      if (this.autoExpand) this.adjustHeight();
      if (!this.characterCounter && !this.presenceRequired) return;

      this.curCharCounter = target.value.trim().length;
      this.updateCharCountMessage();
    },
    // This method is being used in SimpleDescription
    checkCharCount() {
      if (!this.characterCounter && !this.presenceRequired) return;

      this.curCharCounter = this.content.trim().length;
      this.updateCharCountMessage();
    },
    updateCharCountMessage(runOnSubmit = false) {
      if (!this.characterCounter && !this.presenceRequired) {
        return;
      }

      const { curCharCounter, minCharacters, maxCharacters, characterCounter } = this;

      if (curCharCounter < minCharacters) {
        if (runOnSubmit && !characterCounter) {
          this.helperStatus = helperStatus.Error;
          this.helperTextMessage = 'This field is required';
        } else if (runOnSubmit && characterCounter) {
          this.helperStatus = helperStatus.Error;
          this.helperTextMessage = `This field is required and must be at least ${minCharacters} characters`;
        } else if (!runOnSubmit && characterCounter) {
          this.helperStatus = helperStatus.Neutral;
          this.helperTextMessage = `${curCharCounter}/${minCharacters} (minimum)`;
        } else {
          this.helperStatus = helperStatus.Neutral;
          this.helperTextMessage = '';
        }
      } else if (curCharCounter >= minCharacters && curCharCounter <= maxCharacters) {
        this.helperStatus = helperStatus.Valid;
        this.helperTextMessage = `${curCharCounter}/${maxCharacters} (maximum)`;
      } else {
        this.helperStatus = helperStatus.Error;
        this.helperTextMessage = `${curCharCounter}/${maxCharacters} (maximum)`;
      }
    },
    handleEnterKey(event: KeyboardEvent) {
      if (this.preventEnterDefault) {
        event.preventDefault();
      }
      this.$emit('enter');
    },
    checkValidation(): any {
      this.updateCharCountMessage(true);
    },
    resetText() {
      this.content = '';
      this.curCharCounter = 0;
      this.updateCharCountMessage();
    },
  },
};
</script>

<style scoped lang="scss">
.text-area-input {
  background-color: theme('colors.foreground');
  border: 1px solid theme('colors.grey.graphics.subdued');
  border-radius: theme('borderRadius.xl');
  color: theme('colors.grey.body');
  display: block;
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  padding: 1.5rem 1.5rem 0.5rem 1rem;
  letter-spacing: 0.09px;
  line-height: 1.5;
  position: relative;
  resize: vertical;
  width: 100%;
  height: 100%;
  z-index: 0;
  &:focus {
    border: none;
    box-shadow: 0 0 0 2px theme('colors.grey.clickable');
    outline: none;
  }
  &:disabled {
    background-color: theme('colors.grey.background.subdued');
    border: none;
    color: theme('colors.grey.text.subdued');
    cursor: not-allowed;
  }
}
.text-area-error .text-area-input {
  border: none;
  box-shadow: 0 0 0 2px theme('colors.critical.DEFAULT');
  border-radius: theme('borderRadius.xl');
}

.text-area-warn .text-area-input {
  border: none;
  box-shadow: 0 0 0 2px theme('colors.warning.DEFAULT');
  border-radius: theme('borderRadius.xl');
}

.text-area-label {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  left: 1rem;
  top: 1.125rem;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}
textarea:focus ~ .text-area-label {
  font-size: theme('fontSize.xs');
  top: 6px;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
.text-area-label-filled {
  background-color: theme('colors.foreground');
  color: theme('colors.grey.text.subdued');
  display: block;
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.xs');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  padding-top: 7px;
  left: 1rem;
  top: 1px;
  transition:
    top 150ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 150ms cubic-bezier(0.4, 0, 0.2, 1);
  width: calc(100% - 2rem);
  z-index: 1;
}
textarea:disabled ~ .text-area-label-filled {
  background-color: theme('colors.grey.background.subdued');
}
</style>
